import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import { useShopifyCartService } from '@/features/Cart/services/shopifyCart';
import { enqueueCartMutation } from '@/features/Cart/services/cartMutationQueue';
import type { CartMutation } from '@/features/Cart/services/cartMutationResults';
import {
  buildFreeGiftAddition,
  isFreeGiftOptionAvailable,
} from '@/features/Cart/cartRules/freeGift';
import { useProductStockStore } from '@/features/Product/stores/productStock';

type CartFreeGiftSwapOptions = {
  dismissCartNotices: () => void;
  prepareCartForMutation: () => Promise<boolean>;
  mutateAndSync: CartMutation;
};

export const cartFreeGiftSwap = ({
  dismissCartNotices,
  prepareCartForMutation,
  mutateAndSync,
}: CartFreeGiftSwapOptions) => {
  const { updateCart, addToCart } = useShopifyCartService();
  const cartStore = useCartStore();
  const { isFreeGiftItem, getFreeGiftOption, setChosenFreeGiftSku } = cartStore;
  const { cartData, freeGiftItem, freeGiftOptions, chosenFreeGiftSku } = storeToRefs(cartStore);
  const { checkAddStockWithRedraws } = useProductStockStore();

  const getFreeGiftItemSku = (): string | undefined =>
    freeGiftItem.value ? getFreeGiftOption(freeGiftItem.value)?.displaySku : undefined;

  const selectFreeGift = (displaySku: string): Promise<boolean> => {
    dismissCartNotices();
    return enqueueCartMutation(async () => {
      if (!(await prepareCartForMutation())) return false;
      const previousChosenSku = chosenFreeGiftSku.value;
      const currentGiftItem = freeGiftItem.value;
      setChosenFreeGiftSku(displaySku);
      if (!currentGiftItem || getFreeGiftItemSku() === displaySku) return true;

      const option = freeGiftOptions.value.find(
        (giftOption) => giftOption.displaySku === displaySku,
      );
      const nonGiftItems = cartData.value.items.filter((item) => !isFreeGiftItem(item));
      const addition =
        option && isFreeGiftOptionAvailable(option)
          ? buildFreeGiftAddition(option, nonGiftItems)
          : null;
      if (!addition) {
        setChosenFreeGiftSku(previousChosenSku);
        return false;
      }

      const { error } = await mutateAndSync(async () => {
        const { stockCheck, items } = await checkAddStockWithRedraws(
          [addition.item],
          addition.redrawAdd,
        );
        if (stockCheck !== 'available') {
          throw new Error(`Free gift ${displaySku} could not be added: ${stockCheck}`);
        }
        await addToCart(items);
        await updateCart({ [currentGiftItem.key]: 0 });
      }, isFreeGiftItem);
      if (error) console.error('Failed to change free gift', error);

      const isChanged = getFreeGiftItemSku() === displaySku;
      if (!isChanged) setChosenFreeGiftSku(previousChosenSku);
      return isChanged;
    });
  };

  return { selectFreeGift };
};
